import { Injectable } from '@angular/core'
import {
  SavedViewUpdate,
  WorkspaceService,
  WorkspaceUpdate,
} from '../workspace.service'
import {
  SavedView,
  Workspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { AgGridState } from '@northfork/ag-grid-angular'
import { v4 as UUID } from 'uuid'
import { BARE_WORKSPACE } from '../../preset-workspaces/preset-workspaces'
import { storageUpdate$ } from '../ui-storage.service'
import { getObject, putObject } from './local-storage-utils'

/**
 * The Local Storage setup is pretty simple. The key values are like this:
 * {
 *   workspacePrefixes: string[],     // list of all the workspace keys (prefixes).
 *   defaultWorkspacePrefix: string,  // The default workspace key (prefix).
 *   [key: string]: Workspace,        // Where key is the workspace prefix.
 *   [key: string]: string[],         // The key here is in the format of <parentWorkspacePrefix>/savedViewPrefixes.
 *                                    // ^- Get saved view keys (prefixes) contained within this workspace.
 *   [key: string]: SavedView,        // Where the key is the saved view prefix.
 * }
 */
enum WorkspaceKeys {
  WORKSPACE_PREFIXES = 'workspacePrefixes',
  DEFAULT_WORKSPACE_PREFIX = 'defaultWorkspacePrefix',
  SAVED_VIEW_PREFIXES = 'savedViewPrefixes',
}

@Injectable({
  providedIn: 'root',
})
export class LocalWorkspaceService extends WorkspaceService {
  private getUserWorkspacePrefixes(): string[] {
    return getObject<string[]>(WorkspaceKeys.WORKSPACE_PREFIXES) ?? []
  }

  async getUserWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    const prefixesWithWorkspace: string[] = []
    const prefixes = this.getUserWorkspacePrefixes()
    const workspaces: WorkspaceSummary[] = []

    for (const prefix of prefixes) {
      const workspace = await this.getWorkspaceSummary(prefix)

      if (workspace == null) {
        continue
      }

      prefixesWithWorkspace.push(prefix)
      workspaces.push(workspace)
    }

    if (prefixesWithWorkspace.length !== prefixes.length) {
      putObject<string[]>(
        WorkspaceKeys.WORKSPACE_PREFIXES,
        prefixesWithWorkspace,
      )
    }

    return workspaces
  }

  async getWorkspaceSummary(
    workspacePrefix: string,
  ): Promise<WorkspaceSummary | undefined> {
    return this.getWorkspace(workspacePrefix)
  }

  async getWorkspace(workspacePrefix: string): Promise<Workspace | undefined> {
    if (workspacePrefix === BARE_WORKSPACE.workspacePrefix) {
      return BARE_WORKSPACE
    }
    return getObject<Workspace>(workspacePrefix)
  }

  async getDefaultWorkspacePrefix(): Promise<string | undefined> {
    return this.getString(WorkspaceKeys.DEFAULT_WORKSPACE_PREFIX)
  }

  async getDefaultWorkspace(): Promise<Workspace | undefined> {
    const defaultPrefix = await this.getDefaultWorkspacePrefix()
    if (defaultPrefix == null) {
      return undefined
    }

    return this.getWorkspace(defaultPrefix)
  }

  async createWorkspace(
    workspace: Workspace,
    directCopy = false,
  ): Promise<string> {
    const workspaceId = directCopy ? workspace.workspaceId : UUID()
    const workspacePrefix = directCopy
      ? workspace.workspacePrefix
      : this.getNewWorkspacePrefix(workspaceId)

    this.appendObject(WorkspaceKeys.WORKSPACE_PREFIXES, workspacePrefix)
    this.putWorkspace({
      ...workspace,
      workspacePrefix,
      workspaceId,
    })

    return workspacePrefix
  }

  async makeWorkspaceDefault(workspacePrefix: string): Promise<void> {
    const defaultWorkspace = await this.getWorkspace(workspacePrefix)

    if (defaultWorkspace == null) {
      return
    }

    this.putString(WorkspaceKeys.DEFAULT_WORKSPACE_PREFIX, workspacePrefix)

    this.notifyDefaultWorkspaceUpdate()
  }

  async updateWorkspace(
    workspacePrefix: string,
    workspaceUpdate: WorkspaceUpdate,
  ): Promise<void> {
    const workspace = await this.getWorkspace(workspacePrefix)
    if (workspace == null) {
      return
    }

    this.putWorkspace({
      ...workspace,
      ...workspaceUpdate,
    })
  }

  async getPresetWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    return [BARE_WORKSPACE]
  }

  async cloneWorkspace(
    workspacePrefix: string,
    newName?: string,
    newDescription?: string,
  ): Promise<string | undefined> {
    const oldWorkspace = await this.getWorkspace(workspacePrefix)
    if (oldWorkspace == null) {
      return
    }

    const newWorkspacePrefix = await this.createWorkspace({
      ...oldWorkspace,
      name: newName ?? oldWorkspace.name,
      description: newDescription ?? oldWorkspace.description,
    })

    const savedViews = await this.getSavedViews(workspacePrefix)
    for (const savedView of savedViews) {
      await this.createSavedView(
        newWorkspacePrefix,
        savedView.gridKey,
        savedView.savedView,
        true,
      )
    }

    return newWorkspacePrefix
  }

  async deleteWorkspace(workspacePrefix: string): Promise<void> {
    this.deleteObject(workspacePrefix)
    const savedViews = await this.getSavedViews(workspacePrefix)
    for (const savedView of savedViews) {
      this.deleteObject(savedView.savedViewPrefix)
    }
    this.deleteObjectFrom(WorkspaceKeys.WORKSPACE_PREFIXES, workspacePrefix)
    this.deleteObject(this.getSavedViewPrefixesKey(workspacePrefix))
    this.notifyWorkspaceSummaryUpdate(workspacePrefix)
  }

  private getSavedViewPrefixesKey(workspacePrefix: string): string {
    return `${workspacePrefix}/${WorkspaceKeys.SAVED_VIEW_PREFIXES}`
  }

  async getSavedViews(workspacePrefix: string): Promise<SavedView[]> {
    const prefixesWithSavedViews: string[] = []
    const savedViewPrefixesKey = this.getSavedViewPrefixesKey(workspacePrefix)
    const prefixes = getObject<string[]>(savedViewPrefixesKey) ?? []
    const savedViews: SavedView[] = []

    for (const prefix of prefixes) {
      const savedView = this.getSavedView(prefix)

      if (savedView == null) {
        continue
      }

      prefixesWithSavedViews.push(prefix)
      savedViews.push(savedView)
    }

    if (prefixesWithSavedViews.length !== prefixes.length) {
      putObject<string[]>(savedViewPrefixesKey, prefixesWithSavedViews)
    }

    return savedViews
  }

  private getSavedView(savedViewPrefix: string): SavedView | undefined {
    return getObject<SavedView>(savedViewPrefix)
  }

  async updateSavedView(
    savedViewPrefix: string,
    update: SavedViewUpdate,
  ): Promise<void> {
    if (update.savedView == null) return
    const oldSavedView = this.getSavedView(savedViewPrefix)
    if (oldSavedView == null) return

    putObject<SavedView>(savedViewPrefix, {
      ...oldSavedView,
      savedView: update.savedView,
    })
  }

  async createSavedView(
    workspacePrefix: string,
    gridKey: string,
    savedView: AgGridState,
    isPersistent: boolean,
    savedViewIdToCopy: string | undefined = undefined,
    savedViewPrefixToCopy: string | undefined = undefined,
  ): Promise<string> {
    const savedViewId = savedViewIdToCopy ?? gridKey
    const savedViewPrefix =
      savedViewPrefixToCopy ??
      this.getNewSavedViewPrefix(workspacePrefix, gridKey)
    const newSavedView: SavedView = {
      savedViewId,
      savedViewPrefix,
      gridKey,
      savedView,
    }

    if (isPersistent) {
      putObject<SavedView>(savedViewPrefix, newSavedView)
      this.appendObject(
        this.getSavedViewPrefixesKey(workspacePrefix),
        savedViewPrefix,
      )
    }

    return savedViewPrefix
  }

  async cleanUpWorkspaces(
    summariesInHttpStorage: WorkspaceSummary[],
  ): Promise<void> {
    if (summariesInHttpStorage.length === 0) {
      return
    }
    const prefixesInHttpStorage = summariesInHttpStorage.map(
      summary => summary.workspacePrefix,
    )

    for (const prefixInLocalStorage of this.getUserWorkspacePrefixes()) {
      if (prefixesInHttpStorage.includes(prefixInLocalStorage)) {
        continue
      }

      await this.deleteWorkspace(prefixInLocalStorage)
    }
  }

  private getNewWorkspacePrefix(workspaceId: string): string {
    return `localstorage/workspace/${workspaceId}`
  }

  private getNewSavedViewPrefix(
    workspacePrefix: string,
    gridKey: string,
  ): string {
    return `${workspacePrefix}/savedview/localstorage-${gridKey}`
  }

  private getString(key: string): string | undefined {
    return localStorage.getItem(key) ?? undefined
  }

  private putWorkspace(workspace: Workspace): void {
    putObject<Workspace>(workspace.workspacePrefix, workspace)
    this.notifyWorkspaceSummaryUpdate(workspace.workspacePrefix)
  }

  private putString(key: string, value: string): void {
    return localStorage.setItem(key, value)
  }

  private deleteObject(key: string): void {
    localStorage.removeItem(key)
  }

  private appendObject<T>(key: string, object: T): void {
    const objectSet = new Set(getObject<T[]>(key) ?? [])
    objectSet.add(object)

    putObject<T[]>(key, Array.from(objectSet)) //Cannot stringify sets
  }

  private deleteObjectFrom<T>(key: string, object: T): void {
    const objectSet = new Set(getObject<T[]>(key) ?? [])
    objectSet.delete(object)

    putObject<T[]>(key, Array.from(objectSet)) //Cannot stringify sets
  }

  private notifyDefaultWorkspaceUpdate(): void {
    storageUpdate$.next('workspace/default')
  }

  private notifyWorkspaceSummaryUpdate(workspacePrefix: string): void {
    storageUpdate$.next(`${workspacePrefix}/summary`)
  }
}
